import { Plus, Minus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { QuantitySelectorProps } from '../../types';

export function QuantitySelector({
  quantity,
  onQuantityChange,
  min = 1,
  max,
  size = 'md',
  disabled = false,
  showStock = false,
  stock,
  allowManualInput = false,
  allowOnDemand = false
}: QuantitySelectorProps) {
  const [inputValue, setInputValue] = useState(quantity.toString());
  const [isEditing, setIsEditing] = useState(false);

  // Update input value when quantity prop changes
  useEffect(() => {
    if (!isEditing) {
      setInputValue(quantity.toString());
    }
  }, [quantity, isEditing]);

  const handleDecrease = () => {
    const newQuantity = Math.max(min, quantity - 1);
    onQuantityChange(newQuantity);
  };

  const handleIncrease = () => {
    // For on-demand items with no/negative stock, use max or 999
    const effectiveStock = (allowOnDemand && (stock === undefined || stock <= 0)) ? undefined : stock;
    const maxAllowed = max || (effectiveStock ? Math.min(effectiveStock, 999) : 999);
    const newQuantity = Math.min(maxAllowed, quantity + 1);
    onQuantityChange(newQuantity);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    validateAndUpdateQuantity();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setInputValue(quantity.toString());
      setIsEditing(false);
      e.currentTarget.blur();
    }
  };

  const validateAndUpdateQuantity = () => {
    const numValue = parseInt(inputValue, 10);

    if (isNaN(numValue) || numValue < min) {
      // Invalid input, reset to minimum
      const newQuantity = min;
      setInputValue(newQuantity.toString());
      onQuantityChange(newQuantity);
      return;
    }

    // For on-demand items with no/negative stock, use max or 999
    const effectiveStock = (allowOnDemand && (stock === undefined || stock <= 0)) ? undefined : stock;
    const maxAllowed = max || (effectiveStock ? Math.min(effectiveStock, 999) : 999);
    const validQuantity = Math.min(maxAllowed, Math.max(min, numValue));

    setInputValue(validQuantity.toString());
    if (validQuantity !== quantity) {
      onQuantityChange(validQuantity);
    }
  };

  const sizeClasses = {
    sm: {
      button: 'p-1',
      icon: 'w-4 h-4',
      text: 'text-sm font-medium px-2',
      input: 'text-sm font-medium px-2 py-1 w-12 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent',
      container: 'space-x-2'
    },
    md: {
      button: 'p-2',
      icon: 'w-4 h-4',
      text: 'text-sm font-medium px-2 min-w-[2rem] text-center',
      input: 'text-sm font-medium px-2 py-1 w-16 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent',
      container: 'space-x-2'
    },
    lg: {
      button: 'p-3',
      icon: 'w-5 h-5',
      text: 'text-xl font-medium px-6 min-w-[3rem] text-center',
      input: 'text-xl font-medium px-4 py-2 w-20 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent',
      container: 'space-x-4'
    }
  };

  const classes = sizeClasses[size];
  const isDecreaseDisabled = disabled || quantity <= min;

  // For on-demand items with no/negative stock, don't disable based on stock
  const effectiveStock = (allowOnDemand && (stock === undefined || stock <= 0)) ? undefined : stock;
  const isIncreaseDisabled = disabled || (max !== undefined && quantity >= max) || (effectiveStock !== undefined && quantity >= effectiveStock);

  return (
    <div className="space-y-2">
      <div className={`flex items-center ${classes.container}`}>
        <button
          onClick={handleDecrease}
          disabled={isDecreaseDisabled}
          className={`${classes.button} border border-gray-300 rounded-lg hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
        >
          <Minus className={classes.icon} />
        </button>

        {allowManualInput ? (
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            disabled={disabled}
            className={`${classes.input} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            min={min}
            max={max || stock}
          />
        ) : (
          <span className={classes.text}>{quantity}</span>
        )}

        <button
          onClick={handleIncrease}
          disabled={isIncreaseDisabled}
          className={`${classes.button} border border-gray-300 rounded-lg hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
        >
          <Plus className={classes.icon} />
        </button>
      </div>
      {showStock && (
        <p className="text-sm text-gray-500">
          {stock !== undefined && stock > 0 ? `${stock} available` : allowOnDemand ? 'Available on demand' : 'Out of stock'}
        </p>
      )}
    </div>
  );
}
